import { useTableSort } from '@flexinet/ui-components';
import { DataTableSortStatus } from 'mantine-datatable';

import { useApplicationAccountsQuery } from './useApplicationAccountsQuery';
import { useApplicationAccountsSearch } from './useApplicationAccountsSearch';
import { ApplicationAccount } from '../types';

// DEFAULT: newest first, keyed on `created`.
//
// ⚠️ `created` is NULLABLE on ApplicationAccount (types.ts), which is not the
// immutable-AND-non-null key the checklist prefers — a nullable key degrades to
// insertion order if EVERY row lacks it. There is no better option here: `id`
// and `accountId` are the only non-null fields, and sorting a list of accounts
// by an opaque id is meaningless to an operator.
//
// `created` is still the right key. It is immutable (unlike `updated`, which
// would reorder the list under someone reading it), and listAccountApplications
// returns created-descending, so the first paint AGREES with the server rather
// than reshuffling what it returned. Rows missing it sort last, which is correct.
const DEFAULT_SORT_STATUS: DataTableSortStatus<ApplicationAccount> = {
    columnAccessor: 'created',
    direction: 'desc',
};

export const useApplicationAccounts = (applicationId: string) => {

    if (typeof applicationId === 'undefined') {
        throw new Error('applicationId is required parameter');
    }

    // applicationId is a FILTER, and it belongs in the queryKey with every other
    // filter param — it was previously accepted and then dropped, so the list
    // query was unscoped and two applications shared one cache entry.
    const query = useApplicationAccountsQuery({ applicationId });
    const { keyword } = useApplicationAccountsSearch();

    const items: ApplicationAccount[] = query.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) ?? [];

    // ⚠️ Both the keyword filter and the sort below cover only the pages FETCHED
    // SO FAR. Under token pagination the browser cannot filter or reorder rows it
    // has never seen, so the footer states a LOADED count rather than a total and
    // reports `unfilteredCount` alongside the match count, so the operator can see
    // the search was scoped to a window.
    const needle = keyword.trim().toLowerCase();

    const matched = items.filter(item =>
        !needle ||
        item.accountId?.toLowerCase().includes(needle) ||
        item.account?.username?.toLowerCase().includes(needle)
    );

    // Sort state is component-level, not the module-scoped store: sort is how ONE
    // operator is viewing the table right now, unlike the keyword and the filters,
    // which are shared with sibling components.
    const {
        sortStatus,
        setSortStatus,
        sorted: visibleItems,
    } = useTableSort<ApplicationAccount>(matched, DEFAULT_SORT_STATUS);

    const fetchNextPage = () => {
        if (query.hasNextPage) {
            query.fetchNextPage();
        }
    };

    return {
        items,
        visibleItems,
        visibleCount: visibleItems.length,
        unfilteredCount: items.length,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        // FNP-1564: the error itself, not just that there was one.
        //
        // The table could only say "the console API did not return the list",
        // which asserted a cause nobody had established — and was wrong in the
        // case that prompted this: the API DID return a list, alongside a
        // DynamoDBFilterError, because a malformed filter was dropped instead
        // of applied. An operator reading "the API may be unavailable" goes
        // looking at the wrong thing entirely.
        error: query.error,
        isFetchingNextPage: query.isFetchingNextPage,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage,
    };
};
